import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Heart, Sun, Mail, Instagram, Youtube, CalendarDays, Clock, Lock, PlayCircle, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { isInAppBrowser, copyPageLink } from "@/lib/inAppBrowser";
import { Button } from "@/components/ui/button";
import API from "@/lib/api";
import useLiveRefresh from "@/lib/useLiveRefresh";
import PurchaseModal from "@/components/PurchaseModal";
import CoursePurchaseModal from "@/components/CoursePurchaseModal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1643184012410-0d0c9070e575?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxtZWRpdGF0aW9uJTIweW9nYSUyMGhhcHB5JTIwd29tYW4lMjBwZWFjZWZ1bCUyMG5hdHVyZXxlbnwwfHx8fDE3ODgyNDczMzh8MA&ixlib=rb-4.1.0&q=85";
const ABOUT_IMG =
  "https://images.unsplash.com/photo-1593810451410-8fbb422cc15e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxzb3VuZCUyMGJhdGglMjBtaW5kZnVsbmVzcyUyMGVuZXJneSUyMGhlYWxpbmclMjB3b3Jrc2hvcHxlbnwwfHx8fDE3ODgyNDczMzh8MA&ixlib=rb-4.1.0&q=85";

const InAppBanner = () => {
  const [show, setShow] = useState(false);
  useEffect(() => { setShow(isInAppBrowser()); }, []);
  if (!show) return null;
  const copy = async () => {
    const ok = await copyPageLink();
    if (ok) toast.success("Link copied - paste it in Chrome or Safari");
    else toast.error("Could not copy link");
  };
  return (
    <div data-testid="inapp-browser-banner" className="bg-[#96690F] text-[#FDFBF7] px-4 py-2.5 text-center text-xs sm:text-sm leading-relaxed">
      Payments here open Razorpay&apos;s secure page (UPI ID / Card / Netbanking). Prefer UPI apps? Open in{" "}
      <strong>Chrome / Safari</strong>.{" "}
      <button data-testid="copy-link-btn" onClick={copy} className="underline font-semibold">Copy link</button>
    </div>
  );
};

const Navbar = () => (
  <header className="sticky top-0 z-40 backdrop-blur-md bg-[#FDFBF7]/80 border-b border-[rgba(45,90,71,0.15)]">
    <nav className="max-w-6xl mx-auto flex items-center justify-between px-4 sm:px-6 h-16">
      <a href="#top" data-testid="nav-logo" className="flex items-center">
        <img src="/logo.jpeg" alt="Binfinite Mind" className="h-11 mix-blend-multiply" />
      </a>
      <div className="hidden md:flex items-center gap-7 text-sm text-[#5A6E63]">
        <a data-testid="nav-sessions" href="#sessions" className="hover:text-[#2D5A47] transition-colors">Live Sessions</a>
        <a data-testid="nav-courses" href="#courses" className="hover:text-[#2D5A47] transition-colors">Video Courses</a>
        <a data-testid="nav-about" href="#about" className="hover:text-[#2D5A47] transition-colors">About Us</a>
        <a data-testid="nav-contact" href="#contact" className="hover:text-[#2D5A47] transition-colors">Contact Us</a>
        <Link data-testid="nav-consultancy" to="/consultancy" className="hover:text-[#2D5A47] transition-colors">Consultancy</Link>
      </div>
      <div className="flex items-center gap-2">
        <Link to="/dashboard" data-testid="nav-dashboard">
          <Button variant="outline" size="sm" className="rounded-full border-[#2D5A47]/30 text-[#2D5A47] hover:bg-[#E8F3EE]">
            My Sessions
          </Button>
        </Link>
      </div>
    </nav>
    <div className="md:hidden border-t border-[rgba(45,90,71,0.1)] bg-[#FDFBF7]/80">
      <div className="max-w-6xl mx-auto flex items-center justify-center gap-6 px-4 h-10 text-xs text-[#5A6E63]">
        <a data-testid="nav-sessions-mobile" href="#sessions" className="hover:text-[#2D5A47]">Live Sessions</a>
        <a data-testid="nav-courses-mobile" href="#courses" className="hover:text-[#2D5A47]">Courses</a>
        <a data-testid="nav-about-mobile" href="#about" className="hover:text-[#2D5A47]">About Us</a>
        <a data-testid="nav-contact-mobile" href="#contact" className="hover:text-[#2D5A47]">Contact Us</a>
        <Link data-testid="nav-consultancy-mobile" to="/consultancy" className="hover:text-[#2D5A47]">Consultancy</Link>
      </div>
    </div>
  </header>
);

const Hero = ({ image }) => (
  <section id="top" className="relative overflow-hidden grain-overlay pt-10 pb-16 sm:pt-14 sm:pb-24">
    <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#E8F3EE] blur-3xl opacity-70" />
    <div className="relative max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-12 items-center">
      <div className="text-center">
        <p className="text-xs tracking-[0.3em] uppercase text-[#8C7B3F] font-semibold mb-5" data-testid="hero-eyebrow">
          Live Energy Healing Workshops
        </p>
        <h1 data-testid="app-heading" className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-normal leading-[1.35] text-[#96690F] max-w-xl mx-auto">
          Everyone deserves a happy and rich life. Heal your energy—money, health, and
          relationship— Step into a new world.
        </h1>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <a href="#sessions" data-testid="hero-explore-btn">
            <Button className="rounded-full bg-[#2D5A47] hover:bg-[#224536] text-[#FDFBF7] px-7 h-11">
              Explore Live Sessions
            </Button>
          </a>
          <Link to="/dashboard" data-testid="hero-dashboard-btn">
            <Button variant="outline" className="rounded-full border-[#2D5A47]/30 text-[#2D5A47] px-7 h-11 hover:bg-[#E8F3EE]">
              My Classes
            </Button>
          </Link>
        </div>
      </div>
      <div className="relative">
        <img
          src={image || HERO_IMG}
          alt="Peaceful meditation in nature"
          data-testid="hero-image"
          className="soft-shadow w-full aspect-square max-w-md mx-auto rounded-3xl object-cover"
        />
        <div className="absolute -bottom-5 -left-4 sm:-left-8 bg-[#FAF6F0] soft-shadow rounded-2xl px-5 py-4 animate-float-slow">
          <div className="flex items-center gap-3">
            <span className="live-dot w-2.5 h-2.5 rounded-full bg-[#D98E32]" />
            <div>
              <p className="text-sm font-semibold text-[#2D5A47]">Live every week</p>
              <p className="text-xs text-[#5A6E63]">Guided healing from home</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const SessionCard = ({ session, onBuy }) => (
  <article
    data-testid={`session-card-${session.id}`}
    className="group bg-[#FAF6F0] rounded-3xl overflow-hidden soft-shadow border border-[rgba(45,90,71,0.1)] transition-transform duration-300 hover:-translate-y-1.5"
  >
    <div className="relative h-44 overflow-hidden">
      {session.banner_url ? (
        <img
          src={session.banner_url}
          alt={session.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      ) : (
        <div className="w-full h-full bg-[#E8F3EE] flex items-center justify-center">
          <Sparkles className="text-[#2D5A47]/40" size={40} />
        </div>
      )}
      <span className="absolute top-3 left-3 flex items-center gap-1.5 bg-[#FDFBF7]/90 backdrop-blur px-3 py-1 rounded-full text-xs font-semibold text-[#2D5A47]">
        <span className="live-dot w-2 h-2 rounded-full bg-[#D98E32]" /> LIVE
      </span>
      <span className="absolute top-3 right-3 bg-[#2D5A47] text-[#FDFBF7] px-3 py-1 rounded-full text-xs font-semibold">
        {session.category}
      </span>
    </div>
    <div className="p-5">
      <h3 className="font-display text-xl sm:text-2xl font-bold text-[#2D5A47]">{session.title}</h3>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#5A6E63]">
        <span className="flex items-center gap-1"><CalendarDays size={13} /> {session.date}</span>
        <span className="flex items-center gap-1"><Clock size={13} /> {session.time} &middot; {session.duration}</span>
      </div>
      <p className="mt-3 text-sm text-[#5A6E63] leading-relaxed line-clamp-2">{session.description}</p>
      <div className="mt-4 flex items-center justify-between">
        <span className="text-2xl font-bold text-[#1E2A24]">
          &#8377;{session.price}
          <span className="text-xs font-normal text-[#5A6E63]"> /person</span>
        </span>
        <Button
          data-testid={`purchase-btn-${session.id}`}
          onClick={() => onBuy(session)}
          className="rounded-full bg-[#D98E32] hover:bg-[#c17a24] text-[#FDFBF7] px-6 transition-transform duration-200 hover:scale-105"
        >
          Get it
        </Button>
      </div>
    </div>
  </article>
);

const SessionsSection = ({ sessions, loading, onBuy }) => (
  <section id="sessions" className="py-16 sm:py-24 bg-[#E8F3EE]/60">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <p className="text-xs tracking-[0.3em] uppercase text-[#D98E32] font-semibold text-center">Upcoming Live Classes</p>
      <h2 data-testid="sessions-heading" className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-[#2D5A47] text-center mt-3">
        Choose your healing journey
      </h2>
      <p className="text-sm sm:text-base text-[#5A6E63] text-center mt-3 max-w-xl mx-auto">
        Small-group live sessions on Zoom. Book your seat and receive your joining link instantly by email.
      </p>
      {loading ? (
        <p className="text-center text-[#5A6E63] mt-12" data-testid="sessions-loading">Loading sessions...</p>
      ) : sessions.length === 0 ? (
        <p className="text-center text-[#5A6E63] mt-12" data-testid="sessions-empty">New sessions coming soon. Stay tuned.</p>
      ) : (
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {sessions.map((s) => (
            <SessionCard key={s.id} session={s} onBuy={onBuy} />
          ))}
        </div>
      )}
    </div>
  </section>
);

const CourseCard = ({ course, onBuy }) => (
  <article
    data-testid={`course-card-${course.id}`}
    className="group bg-[#FDFBF7] rounded-3xl overflow-hidden soft-shadow border border-[rgba(45,90,71,0.1)] transition-transform duration-300 hover:-translate-y-1.5"
  >
    <div className="relative h-44 overflow-hidden">
      {course.banner_url ? (
        <img src={course.banner_url} alt={course.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
      ) : (
        <div className="w-full h-full bg-[#E8F3EE] flex items-center justify-center">
          <BookOpen className="text-[#2D5A47]/40" size={40} />
        </div>
      )}
      <span className="absolute top-3 left-3 flex items-center gap-1.5 bg-[#FDFBF7]/90 backdrop-blur px-3 py-1 rounded-full text-xs font-semibold text-[#2D5A47]">
        <Lock size={12} /> LOCKED
      </span>
      <span className="absolute top-3 right-3 bg-[#2D5A47] text-[#FDFBF7] px-3 py-1 rounded-full text-xs font-semibold">
        {course.lesson_count} lesson{course.lesson_count === 1 ? "" : "s"}
      </span>
    </div>
    <div className="p-5">
      <h3 className="font-display text-xl sm:text-2xl font-bold text-[#2D5A47]">{course.title}</h3>
      <p className="mt-3 text-sm text-[#5A6E63] leading-relaxed line-clamp-2">{course.description}</p>
      {course.lessons?.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {course.lessons.slice(0, 3).map((l, i) => (
            <li key={i} className="flex items-center gap-2 text-xs text-[#5A6E63]">
              <PlayCircle size={14} className="text-[#D98E32] shrink-0" />
              <span className="truncate">{l.title}</span>
            </li>
          ))}
          {course.lessons.length > 3 && (
            <li className="text-xs text-[#8a9a90] pl-6">+ {course.lessons.length - 3} more lesson(s)</li>
          )}
        </ul>
      )}
      <div className="mt-4 flex items-center justify-between">
        <span className="text-2xl font-bold text-[#1E2A24]">
          &#8377;{course.price}
          <span className="text-xs font-normal text-[#5A6E63]"> /lifetime</span>
        </span>
        <Button
          data-testid={`course-buy-btn-${course.id}`}
          onClick={() => onBuy(course)}
          className="rounded-full bg-[#D98E32] hover:bg-[#c17a24] text-[#FDFBF7] px-5 transition-transform duration-200 hover:scale-105"
        >
          <Lock size={14} className="mr-1.5" /> Buy &amp; Unlock
        </Button>
      </div>
    </div>
  </article>
);

const CoursesSection = ({ courses, loading, onBuy }) => (
    <section id="courses" className="py-16 sm:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <p className="text-xs tracking-[0.3em] uppercase text-[#D98E32] font-semibold text-center">Learn At Your Own Pace</p>
        <h2 data-testid="courses-heading" className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-[#2D5A47] text-center mt-3">
          Video Courses
        </h2>
        <p className="text-sm sm:text-base text-[#5A6E63] text-center mt-3 max-w-xl mx-auto">
          Self-paced video courses you can watch anytime. Every lesson stays locked until you unlock lifetime access.
        </p>
        {loading ? (
          <p className="text-center text-[#5A6E63] mt-12" data-testid="courses-loading">Loading courses...</p>
        ) : courses.length === 0 ? (
          <p className="text-center text-[#5A6E63] mt-12" data-testid="courses-empty">New video courses coming soon. Stay tuned.</p>
        ) : (
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((c) => (
              <CourseCard key={c.id} course={c} onBuy={onBuy} />
            ))}
          </div>
        )}
      </div>
    </section>
);

const AboutSection = () => (  <section id="about" className="py-16 sm:py-24">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-12 items-center">
      <img src={ABOUT_IMG} alt="Sound bath energy healing" data-testid="about-image" className="rounded-3xl soft-shadow w-full h-72 sm:h-96 object-cover" />
      <div>
        <p className="text-xs tracking-[0.3em] uppercase text-[#D98E32] font-semibold">About Us</p>
        <h2 data-testid="about-heading" className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-[#2D5A47] mt-3">
          Healing energy for a happy, rich life
        </h2>
        <p className="mt-5 text-sm sm:text-base text-[#5A6E63] leading-relaxed" data-testid="about-text">
          Binfinite Mind is a sanctuary for anyone seeking a worry free life. Through live guided
          energy-healing workshops, we help you release blocks around money, health and
          relationships, so you can step into a calmer, more abundant world. Join thousands who
          have transformed anxiety into peace - from the comfort of home.
        </p>
        <div className="mt-7 grid grid-cols-3 gap-4">
          {[
            { icon: Sparkles, label: "Guided Energy Healing" },
            { icon: Heart, label: "Money, Health & Love" },
            { icon: Sun, label: "Happy Vibes Always" },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="bg-[#E8F3EE] rounded-2xl p-4 text-center">
              <Icon className="mx-auto text-[#2D5A47]" size={20} />
              <p className="mt-2 text-xs font-medium text-[#2D5A47]">{label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const ContactSection = () => (
  <section id="contact" className="py-16 sm:py-24 bg-[#F9EBEA]/60">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
      <p className="text-xs tracking-[0.3em] uppercase text-[#D98E32] font-semibold">Contact Us</p>
      <h2 data-testid="contact-heading" className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-[#2D5A47] mt-3">
        We would love to hear from you
      </h2>
      <div className="mt-10 grid sm:grid-cols-3 gap-5 max-w-3xl mx-auto">
        {[
          { icon: Mail, label: "Email", value: "binfinitemind@gmail.com", href: "mailto:binfinitemind@gmail.com", testid: "contact-email" },
          { icon: Instagram, label: "Instagram", value: "@binfinitemind", href: "https://www.instagram.com/binfinitemind?igsi=MWpuZ2l6dGlmdDQ5eA%3D%3D&utm_source=qr", testid: "contact-instagram" },
          { icon: Youtube, label: "YouTube", value: "@binfinitemind", href: "https://youtube.com/@binfinitemind?si=eHV6UnrjtLFmtGHe", testid: "contact-youtube" },
        ].map(({ icon: Icon, label, value, href, testid }) => (
          <a key={label} href={href} target="_blank" rel="noopener noreferrer" data-testid={testid}
            className="bg-[#FAF6F0] rounded-2xl p-6 soft-shadow block transition-transform duration-200 hover:-translate-y-1">
            <Icon className="mx-auto text-[#D98E32]" size={22} />
            <p className="mt-3 text-xs uppercase tracking-widest text-[#5A6E63]">{label}</p>
            <p className="mt-1 text-sm font-semibold text-[#2D5A47]">{value}</p>
          </a>
        ))}
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="py-12 bg-[#2D5A47]">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
      <div className="inline-flex items-center bg-[#FDFBF7] rounded-full px-6 py-2">
        <img src="/logo.jpeg" alt="Binfinite Mind" className="h-9" />
      </div>
      <p data-testid="footer-ending-text" className="mt-4 text-base md:text-lg text-[#E8F3EE] italic font-display">
        "Live a worry free life"
      </p>
      <div className="mt-6 flex items-center justify-center gap-6 text-xs text-[#E8F3EE]/70">
        <a href="#sessions" className="hover:text-[#FDFBF7]">Sessions</a>
        <a href="#about" className="hover:text-[#FDFBF7]">About</a>
        <a href="#contact" className="hover:text-[#FDFBF7]">Contact</a>
      </div>
    </div>
  </footer>
);

export default function Landing() {
  const [sessions, setSessions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [heroImage, setHeroImage] = useState("");

  useLiveRefresh(() => {
    API.get("/sessions")
      .then((res) => setSessions(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    API.get("/courses")
      .then((res) => setCourses(res.data))
      .catch(() => {})
      .finally(() => setCoursesLoading(false));
    API.get("/site-config")
      .then((res) => setHeroImage(res.data.hero_image || ""))
      .catch(() => {});
  });

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="landing-page">
      <InAppBanner />
      <Navbar />
      <Hero image={heroImage} />
      <SessionsSection sessions={sessions} loading={loading} onBuy={setSelected} />
      <CoursesSection courses={courses} loading={coursesLoading} onBuy={setSelectedCourse} />
      <AboutSection />
      <ContactSection />
      <Footer />
      <PurchaseModal session={selected} onClose={() => setSelected(null)} />
      <CoursePurchaseModal course={selectedCourse} onClose={() => setSelectedCourse(null)} />
    </div>
  );
}
